import React, { useState, useCallback, useMemo, useEffect } from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
} from "recharts";
import {
  Upload,
  AlertTriangle,
  Lock,
  Eye,
  TrendingUp,
  RefreshCw,
  Database,
  FileSpreadsheet,
  Search,
  ChevronDown,
} from "lucide-react";
import PIIProtectionTab from "./PiiProtection";

const DUMMY_DATA_SOURCES = [
  {
    id: 1,
    name: "CrateDB",
    description: "Primary CrateDb database for customer data",
    type: "CrateDB",
    createdAt: "2024-01-15",
    status: "Active",
    connectionString: "postgres://localhost:5432/customers",
  },
  {
    id: 2,
    name: "Customer Database",
    description: "Primary PostgreSQL database for customer data",
    type: "PostgreSQL",
    createdAt: "2024-01-15",
    status: "Active",
    connectionString: "postgres://localhost:5432/customers",
  },
  {
    id: 3,
    name: "Sales Data Warehouse",
    description: "MySQL warehouse for sales analytics",
    type: "MySQL",
    createdAt: "2024-01-10",
    status: "Active",
    connectionString: "mysql://localhost:3306/sales",
  },
];

const PII = () => {
  const [activeTab, setActiveTab] = useState("pii-detection");
  const [selectedSource, setSelectedSource] = useState(null);
  const [selectedTable, setSelectedTable] = useState(null);
  const [files, setFiles] = useState([]);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [apiData, setApiData] = useState(null);
  const [piiSessionId, setPiiSessionId] = useState(null);
  const [piiFileName, setPiiFileName] = useState(null);
  const [piiDetectionData, setPiiDetectionData] = useState(null);
  const [piiAnalysisComplete, setPiiAnalysisComplete] = useState(false);
  const [crateDbTables, setCrateDbTables] = useState([]);
  const [isSourceDropdownOpen, setIsSourceDropdownOpen] = useState(false);
  const [isTableDropdownOpen, setIsTableDropdownOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [searchTableTerm, setSearchTableTerm] = useState("");

  const fetchCrateDBTables = async () => {
    try {
      const response = await fetch(
        "https://apiowlsight.bluegecko.ai:8003/api/v1/cratedb/tables?encrypted_only=false"
      );
      const jsonResp = await response.json();
      if (jsonResp.success && Array.isArray(jsonResp.tables)) {
        setCrateDbTables(jsonResp.tables || []);
      } else {
        setCrateDbTables([]);
      }
    } catch (error) {
      console.error("Error fetching CrateDB tables:", error);
      setCrateDbTables([]);
    }
  };

  useEffect(() => {
    if (selectedSource && selectedSource.name.toLowerCase() === "cratedb") {
      fetchCrateDBTables();
    }
  }, [selectedSource]);

  const handleFileUpload = useCallback((event) => {
    const uploadedFiles = event.target.files;
    if (!uploadedFiles || uploadedFiles.length === 0) return;

    const fileList = Array.from(uploadedFiles);
    setFiles(fileList);
    setApiData(null);
    setSelectedSource(null);
    setSelectedTable(null);
  }, []);

  const getRiskColor = (riskLevel) => {
    switch (riskLevel?.toLowerCase()) {
      case "minimal":
        return "#22c55e";
      case "low":
        return "#10b981";
      case "medium":
        return "#f59e0b";
      case "high":
        return "#ef4444";
      default:
        return "#6c757d";
    }
  };

  const riskAssessmentChartData = useMemo(() => {
    if (!apiData?.risk_assessment?.visualizations) return null;

    const pieData = apiData.risk_assessment.visualizations.pie_chart?.data;
    const barData = apiData.risk_assessment.visualizations.bar_chart;

    return {
      pie: pieData || [],
      bar: {
        categories: barData?.categories || [],
        data: barData?.data || [],
        colors: barData?.colors || [],
      },
    };
  }, [apiData?.risk_assessment?.visualizations]);

  const complianceChartsData = useMemo(() => {
    if (!apiData?.compliance?.visualizations) return null;

    const barChart = apiData.compliance.visualizations.comparison_bar_chart;

    return {
      categories: barChart?.categories || ["GDPR", "HIPAA", "CCPA"],
      data: barChart?.data || [0, 0, 0],
      colors: barChart?.colors || ["#3b82f6", "#14b8a6", "#f97316"],
    };
  }, [apiData?.compliance?.visualizations]);

  const callPIIDetectionAPI = async (file) => {
    try {
      const formData = new FormData();
      formData.append("files", file);
      formData.append("sample_limit", 1200);
      formData.append("language", "auto");
      formData.append("has_encryption", true);
      formData.append("has_activity_logs", true);
      formData.append("has_deletion_policy", true);
      formData.append("has_rbac", true);

      const response = await fetch(
        "https://apiowlsight.bluegecko.ai:8003/api/v1/analyze-complete",
        {
          method: "POST",
          body: formData,
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      if (data.success) {
        setPiiSessionId(data.session_id);
        setPiiFileName(data.file_name);
        setPiiAnalysisComplete(true);
      }

      if (data && !data.compliance?.summary) {
        data.compliance = data.compliance || {};
        data.compliance.summary = {
          overall_status: "Compliant",
          average_score: 100,
        };
      }

      return data;
    } catch (error) {
      console.error("Error calling PII Detection API:", error);
      throw error;
    }
  };

  const callPIIDetectionAPIFromTable = async (tableName) => {
    try {
      const formData = new URLSearchParams();
      formData.append("table_name", tableName);
      formData.append("schema_name", "doc");

      const response = await fetch(
        "https://apiowlsight.bluegecko.ai:8003/api/v1/analyze-complete",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/x-www-form-urlencoded",
          },
          body: formData.toString(),
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      if (data.success) {
        setPiiSessionId(data.session_id);
        setPiiFileName(data.file_name || tableName);
        setPiiAnalysisComplete(true);
      }

      if (data && !data.compliance?.summary) {
        data.compliance = data.compliance || {};
        data.compliance.summary = {
          overall_status: "Compliant",
          average_score: 100,
        };
      }

      return data;
    } catch (error) {
      console.error("Error calling PII Detection API from table:", error);
      throw error;
    }
  };

  const analyzePIIDetection = useCallback(async () => {
    if (
      selectedSource &&
      selectedSource.name.toLowerCase() === "cratedb" &&
      !selectedTable
    ) {
      return;
    }

    if (!selectedSource && files.length === 0) {
      return;
    }

    setIsAnalyzing(true);

    try {
      let piiResponse;
      
      if (
        selectedSource &&
        selectedSource.name.toLowerCase() === "cratedb" &&
        selectedTable
      ) {
        // Use table name from selected table
        const tableName = selectedTable.value || selectedTable.label || selectedTable;
        piiResponse = await callPIIDetectionAPIFromTable(tableName);
      } else if (files.length > 0) {
        piiResponse = await callPIIDetectionAPI(files[0]);
      }

      if (piiResponse) {
        setApiData(piiResponse);
        setPiiDetectionData(piiResponse);
      }
    } catch (error) {
      console.error("Analysis error:", error);
    } finally {
      setIsAnalyzing(false);
    }
  }, [files, selectedSource, selectedTable]);

  const tabs = [
    { id: "pii-detection", label: "🔐 PII Detection", icon: Lock },
    { id: "pii-protection", label: "🛡️ PII Protection", icon: Eye },
  ];

  return (
    <div className="space-y-6">
      {/* Tab Navigation */}
      <div className="flex gap-1 border-b border-gray-200">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            onClick={() => {
              if (tab.id === "pii-protection" && !piiAnalysisComplete) {
                // Don't switch if analysis not complete
                return;
              }
              setActiveTab(tab.id);
            }}
            className={`px-4 py-2 font-semibold text-xs transition-all border-b-2 ${
              activeTab === tab.id
                ? "text-blue-600 border-blue-600 bg-gradient-to-b from-blue-50 to-transparent"
                : tab.id === "pii-protection" && !piiAnalysisComplete
                ? "text-gray-400 border-transparent cursor-not-allowed"
                : "text-gray-600 border-transparent hover:text-gray-900 hover:border-gray-300"
            }`}
            disabled={tab.id === "pii-protection" && !piiAnalysisComplete}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* PII Detection Tab */}
      {activeTab === "pii-detection" && (
        <div className="space-y-8">
          {/* Control Bar */}
          <div className="bg-gradient-to-r from-gray-50 to-gray-100 p-3 rounded-xl border border-gray-200 space-y-2">
            <div className="grid grid-cols-12 gap-3">
              {/* Data Source */}
              <div className="col-span-3 relative">
                <label className="block text-xs font-semibold text-gray-600 mb-1 uppercase tracking-wider">
                  Data Source
                </label>

                <div
                  className="flex items-center justify-between px-3 py-2 bg-white rounded-lg border border-gray-300 cursor-pointer hover:border-blue-400 transition-colors shadow-sm text-sm"
                  onClick={() => setIsSourceDropdownOpen(!isSourceDropdownOpen)}
                >
                  <div className="flex items-center gap-2">
                    <Database className="w-3 h-3 text-blue-600" />
                    <span className="text-xs font-medium text-gray-700">
                      {selectedSource ? selectedSource.name : "Select"}
                    </span>
                  </div>
                  <ChevronDown
                    className={`w-3 h-3 text-gray-400 transition-transform ${
                      isSourceDropdownOpen ? "rotate-180" : ""
                    }`}
                  />
                </div>

                {isSourceDropdownOpen && (
                  <div className="absolute top-full mt-1 w-full bg-white rounded-lg border border-gray-200 shadow-2xl z-50">
                    <div className="p-2 border-b border-gray-100">
                      <div className="relative">
                        <Search className="w-3 h-3 text-gray-400 absolute left-2 top-2" />
                        <input
                          type="text"
                          placeholder="Search..."
                          className="w-full pl-7 pr-3 py-1.5 text-xs border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          value={searchTerm}
                          onChange={(e) => setSearchTerm(e.target.value)}
                        />
                      </div>
                    </div>

                    <div className="max-h-40 overflow-y-auto">
                      {DUMMY_DATA_SOURCES.filter(
                        (source) =>
                          source.status === "Active" &&
                          (source.name
                            .toLowerCase()
                            .includes(searchTerm.toLowerCase()) ||
                            source.type
                              .toLowerCase()
                              .includes(searchTerm.toLowerCase()))
                      ).map((source) => (
                        <div
                          key={source.id}
                          className="px-3 py-2 hover:bg-blue-50 cursor-pointer border-b border-gray-100 transition-colors last:border-0"
                          onClick={() => {
                            setSelectedSource(source);
                            setIsSourceDropdownOpen(false);
                            setSearchTerm("");
                            setSelectedTable(null);
                            setFiles([]);
                          }}
                        >
                          <div className="flex items-center gap-2">
                            <div className="w-1.5 h-1.5 bg-green-500 rounded-full"></div>
                            <div>
                              <p className="text-xs font-semibold text-gray-900">
                                {source.name}
                              </p>
                              <p className="text-xs text-gray-500">{source.type}</p>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Table Dropdown - Only if CrateDB selected */}
              {selectedSource &&
                selectedSource.name.toLowerCase() === "cratedb" && (
                  <div className="col-span-3 relative">
                    <label className="block text-xs font-semibold text-gray-600 mb-1 uppercase tracking-wider">
                      Table Name
                    </label>
                    <div
                      className="flex items-center justify-between px-3 py-2 bg-white rounded-lg border border-gray-300 cursor-pointer hover:border-blue-400 transition-colors shadow-sm text-sm"
                      onClick={() => setIsTableDropdownOpen(!isTableDropdownOpen)}
                    >
                      <div className="flex items-center gap-2">
                        <FileSpreadsheet className="w-3 h-3 text-blue-600" />
                        <span className="text-xs font-medium text-gray-700">
                          {selectedTable
                            ? selectedTable.label || selectedTable.value || selectedTable
                            : "Select"}
                        </span>
                      </div>
                      <ChevronDown
                        className={`w-3 h-3 text-gray-400 transition-transform ${
                          isTableDropdownOpen ? "rotate-180" : ""
                        }`}
                      />
                    </div>

                    {isTableDropdownOpen && (
                      <div className="absolute top-full mt-1 w-full bg-white rounded-lg border border-gray-200 shadow-2xl z-50">
                        <div className="p-2 border-b border-gray-100">
                          <div className="relative">
                            <Search className="w-3 h-3 text-gray-400 absolute left-2 top-2" />
                            <input
                              type="text"
                              placeholder="Search..."
                              className="w-full pl-7 pr-3 py-1.5 text-xs border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                              value={searchTableTerm}
                              onChange={(e) =>
                                setSearchTableTerm(e.target.value)
                              }
                            />
                          </div>
                        </div>

                        <div className="max-h-40 overflow-y-auto">
                          {crateDbTables
                            .filter((table) =>
                              (table.label || table.value || table)
                                .toLowerCase()
                                .includes(searchTableTerm.toLowerCase())
                            )
                            .map((table, idx) => (
                              <div
                                key={idx}
                                className="px-3 py-2 hover:bg-blue-50 cursor-pointer border-b border-gray-100 transition-colors last:border-0"
                                onClick={() => {
                                  setSelectedTable(table);
                                  setIsTableDropdownOpen(false);
                                  setSearchTableTerm("");
                                }}
                              >
                                <p className="text-xs font-medium text-gray-900">
                                  {table.label || table.value || table}
                                </p>
                                {table.schema && (
                                  <p className="text-xs text-gray-500">
                                    Schema: {table.schema}
                                  </p>
                                )}
                              </div>
                            ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}

              {/* File Upload */}
              <div className="col-span-3">
                <label className="block text-xs font-semibold text-gray-600 mb-1 uppercase tracking-wider">
                  {selectedSource?.name?.toLowerCase() === "cratedb"
                    ? "Table or File"
                    : "Upload Files"}
                </label>

                {selectedSource?.name?.toLowerCase() === "cratedb" ? (
                  <button
                    onClick={() => {
                      setSelectedSource(null);
                      setSelectedTable(null);
                      setFiles([]);
                      setApiData(null);
                    }}
                    className="w-full flex items-center justify-between px-3 py-2 bg-white rounded-lg border border-gray-300 cursor-pointer hover:border-red-400 transition-colors shadow-sm text-sm"
                  >
                    <div className="flex items-center gap-2">
                      <Upload className="w-3 h-3 text-red-600" />
                      <span className="text-xs font-medium text-gray-700">
                        Switch to File Upload
                      </span>
                    </div>
                    <ChevronDown className="w-3 h-3 text-gray-400" />
                  </button>
                ) : (
                  <label className="flex items-center justify-between px-3 py-2 bg-white rounded-lg border border-gray-300 cursor-pointer hover:border-blue-400 transition-colors shadow-sm text-sm">
                    <div className="flex items-center gap-2">
                      <Upload className="w-3 h-3 text-blue-600" />
                      <span className="text-xs font-medium text-gray-700">
                        {files.length > 0
                          ? `${files.length} file(s) selected`
                          : "Choose Files"}
                      </span>
                    </div>

                    <input
                      type="file"
                      className="hidden"
                      accept=".xlsx,.xls,.csv"
                      onChange={handleFileUpload}
                      multiple
                      key={files.length}
                    />
                  </label>
                )}
              </div>

              {/* Analyze Button */}
              <div className="col-span-3 flex items-end">
                <button
                  onClick={analyzePIIDetection}
                  disabled={
                    isAnalyzing ||
                    (selectedSource?.name.toLowerCase() === "cratedb"
                      ? !selectedTable
                      : files.length === 0)
                  }
                  className={`w-full px-3 py-2 rounded-lg flex items-center justify-center gap-2 font-semibold text-xs transition-all ${
                    isAnalyzing ||
                    (selectedSource?.name.toLowerCase() === "cratedb"
                      ? !selectedTable
                      : files.length === 0)
                      ? "bg-gray-300 text-gray-500 cursor-not-allowed"
                      : "bg-gradient-to-r from-blue-600 to-blue-700 text-white hover:shadow-lg"
                  }`}
                >
                  {isAnalyzing ? (
                    <>
                      <RefreshCw className="w-3 h-3 animate-spin" />
                      Analyzing
                    </>
                  ) : (
                    <>
                      <TrendingUp className="w-3 h-3" />
                      Analyze
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>

          {/* Dashboard Content */}
          {!apiData && files.length === 0 && !selectedTable ? (
            <div className="flex items-center justify-center min-h-[60vh]">
              <div className="text-center bg-white p-16 rounded-3xl border border-gray-200 max-w-md w-full shadow-lg">
                <div className="w-20 h-20 bg-gradient-to-br from-teal-100 to-cyan-100 rounded-2xl flex items-center justify-center mx-auto mb-6">
                  <Lock className="w-10 h-10 text-teal-600" />
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-3">
                  PII Detection Analysis
                </h3>
                <p className="text-gray-600 leading-relaxed">
                  Upload files to detect PII data, assess privacy risks, and
                  check compliance
                </p>
              </div>
            </div>
          ) : (
            <>
              {apiData && (
                <>
                  {/* Stats Grid */}
                  <div className="grid grid-cols-4 gap-4">
                    <div className="bg-white rounded-lg p-4 border border-gray-200 shadow-sm hover:border-teal-400 hover:shadow-md transition-all">
                      <p className="text-gray-600 text-[10px] font-semibold uppercase tracking-wider mb-1">
                        Total Columns
                      </p>
                      <p className="text-3xl font-bold text-blue-600">
                        {apiData.pii_detection.total_columns}
                      </p>
                      <p className="text-gray-500 text-[11px] mt-1">Analyzed</p>
                    </div>

                    <div className="bg-white rounded-lg p-4 border border-gray-200 shadow-sm hover:border-red-400 hover:shadow-md transition-all">
                      <p className="text-gray-600 text-[10px] font-semibold uppercase tracking-wider mb-1">
                        PII Columns
                      </p>
                      <p className="text-3xl font-bold text-red-600">
                        {apiData.pii_detection.pii_column_count}
                      </p>
                      <p className="text-gray-500 text-[11px] mt-1">Detected</p>
                    </div>

                    <div className="bg-white rounded-lg p-4 border border-gray-200 shadow-sm hover:border-orange-400 hover:shadow-md transition-all">
                      <p className="text-gray-600 text-[10px] font-semibold uppercase tracking-wider mb-1">
                        Risk Score
                      </p>
                      <p
                        className="text-3xl font-bold"
                        style={{
                          color: getRiskColor(
                            apiData.risk_assessment.risk_level
                          ),
                        }}
                      >
                        {apiData.risk_assessment.overall_score}
                      </p>
                      <p className="text-gray-500 text-[11px] mt-1">
                        {apiData.risk_assessment.risk_level}
                      </p>
                    </div>

                    <div className="bg-white rounded-lg p-4 border border-gray-200 shadow-sm hover:border-green-400 hover:shadow-md transition-all">
                      <p className="text-gray-600 text-[10px] font-semibold uppercase tracking-wider mb-1">
                        Overall Status
                      </p>
                      <p className="text-3xl font-bold text-green-600">
                        {apiData.compliance.summary.overall_status}
                      </p>
                      <p className="text-gray-500 text-[11px] mt-1">
                        All Regulations
                      </p>
                    </div>
                  </div>

                  {/* Risk Assessment & Compliance Charts */}
                  <div className="grid grid-cols-2 gap-4">
                    {/* Risk Breakdown - Pie Chart */}
                    <div className="bg-white rounded-lg p-4 border border-gray-200 shadow-sm">
                      <h3 className="text-sm font-bold text-gray-900 mb-3">
                        Risk Breakdown Distribution
                      </h3>

                      {riskAssessmentChartData?.pie &&
                      riskAssessmentChartData.pie.length > 0 ? (
                        <ResponsiveContainer width="100%" height={250}>
                          <PieChart>
                            <Pie
                              data={riskAssessmentChartData.pie}
                              cx="50%"
                              cy="50%"
                              labelLine={false}
                              label={({ name, value }) => `${name}: ${value}`}
                              outerRadius={80}
                              dataKey="value"
                            >
                              {riskAssessmentChartData.pie.map(
                                (entry, index) => (
                                  <Cell
                                    key={`cell-${index}`}
                                    fill={
                                      ["#ef4444", "#f59e0b", "#6c757d"][
                                        index
                                      ] || "#6c757d"
                                    }
                                  />
                                )
                              )}
                            </Pie>
                            <Tooltip
                              contentStyle={{
                                backgroundColor: "#fff",
                                border: "1px solid #e5e7eb",
                                borderRadius: "6px",
                                fontSize: "12px",
                              }}
                            />
                          </PieChart>
                        </ResponsiveContainer>
                      ) : (
                        <div className="h-56 flex items-center justify-center text-gray-400 text-sm">
                          No risk data available
                        </div>
                      )}
                    </div>

                    {/* Risk Level by Category - Bar Chart */}
                    <div className="bg-white rounded-lg p-4 border border-gray-200 shadow-sm">
                      <h3 className="text-sm font-bold text-gray-900 mb-3">
                        Columns by Risk Level
                      </h3>

                      {riskAssessmentChartData?.bar &&
                      riskAssessmentChartData.bar.categories.length > 0 ? (
                        <ResponsiveContainer width="100%" height={250}>
                          <BarChart
                            data={riskAssessmentChartData.bar.categories.map(
                              (category, idx) => ({
                                name: category,
                                count:
                                  riskAssessmentChartData.bar.data[idx] || 0,
                                fill:
                                  riskAssessmentChartData.bar.colors[idx] ||
                                  "#6c757d",
                              })
                            )}
                          >
                            <CartesianGrid
                              strokeDasharray="3 3"
                              stroke="#f0f0f0"
                            />
                            <XAxis dataKey="name" tick={{ fontSize: 10 }} />
                            <YAxis tick={{ fontSize: 10 }} />
                            <Tooltip
                              contentStyle={{
                                backgroundColor: "#fff",
                                border: "1px solid #e5e7eb",
                                borderRadius: "6px",
                                fontSize: "12px",
                              }}
                            />
                            <Bar dataKey="count" radius={[4, 4, 0, 0]}>
                              {riskAssessmentChartData.bar.categories.map(
                                (_, index) => (
                                  <Cell
                                    key={`bar-${index}`}
                                    fill={
                                      riskAssessmentChartData.bar.colors[
                                        index
                                      ] || "#6c757d"
                                    }
                                  />
                                )
                              )}
                            </Bar>
                          </BarChart>
                        </ResponsiveContainer>
                      ) : (
                        <div className="h-56 flex items-center justify-center text-gray-400 text-sm">
                          No risk level data available
                        </div>
                      )}
                    </div>

                    {/* Risk Assessment Metrics Card */}
                    <div className="bg-white rounded-lg p-4 border border-gray-200 shadow-sm">
                      <h3 className="text-sm font-bold text-gray-900 mb-3">
                        Risk Assessment Details
                      </h3>
                      {apiData?.risk_assessment?.metrics && (
                        <div className="space-y-3">
                          <div className="flex justify-between items-center p-2 bg-gray-50 rounded">
                            <span className="text-xs text-gray-600">
                              High Risk Columns:
                            </span>
                            <span className="text-sm font-bold text-red-600">
                              {apiData.risk_assessment.metrics
                                .high_risk_columns || 0}
                            </span>
                          </div>
                          <div className="flex justify-between items-center p-2 bg-gray-50 rounded">
                            <span className="text-xs text-gray-600">
                              Medium Risk Columns:
                            </span>
                            <span className="text-sm font-bold text-yellow-600">
                              {apiData.risk_assessment.metrics
                                .medium_risk_columns || 0}
                            </span>
                          </div>
                          <div className="flex justify-between items-center p-2 bg-gray-50 rounded">
                            <span className="text-xs text-gray-600">
                              Low Risk Columns:
                            </span>
                            <span className="text-sm font-bold text-green-600">
                              {apiData.risk_assessment.metrics
                                .low_risk_columns || 0}
                            </span>
                          </div>
                          <div className="mt-4 p-3 bg-gradient-to-r from-teal-50 to-cyan-50 rounded-lg border border-teal-200">
                            <p className="text-xs font-semibold text-teal-900 mb-2">
                              Overall Risk Level
                            </p>
                            <p
                              className="text-lg font-bold"
                              style={{
                                color: getRiskColor(
                                  apiData.risk_assessment.risk_level
                                ),
                              }}
                            >
                              {apiData.risk_assessment.risk_level || "N/A"}
                            </p>
                            <p className="text-xs text-teal-700 mt-1">
                              Score:{" "}
                              {apiData.risk_assessment.overall_score || 0}
                            </p>
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Compliance Comparison - Bar Chart */}
                    <div className="bg-white rounded-lg p-4 border border-gray-200 shadow-sm">
                      <h3 className="text-sm font-bold text-gray-900 mb-3">
                        Compliance Scores Comparison
                      </h3>

                      {complianceChartsData &&
                      complianceChartsData.categories.length > 0 ? (
                        <ResponsiveContainer width="100%" height={250}>
                          <BarChart
                            data={complianceChartsData.categories.map(
                              (category, idx) => ({
                                name: category,
                                score: complianceChartsData.data[idx] || 0,
                                fill:
                                  complianceChartsData.colors[idx] || "#6c757d",
                              })
                            )}
                          >
                            <CartesianGrid
                              strokeDasharray="3 3"
                              stroke="#f0f0f0"
                            />
                            <XAxis dataKey="name" tick={{ fontSize: 10 }} />
                            <YAxis
                              domain={[0, 100]}
                              tick={{ fontSize: 10 }}
                            />
                            <Tooltip
                              contentStyle={{
                                backgroundColor: "#fff",
                                border: "1px solid #e5e7eb",
                                borderRadius: "6px",
                                fontSize: "12px",
                              }}
                              formatter={(value) => `${value}%`}
                            />
                            <Bar dataKey="score" radius={[4, 4, 0, 0]}>
                              {complianceChartsData.categories.map(
                                (_, index) => (
                                  <Cell
                                    key={`compliance-${index}`}
                                    fill={
                                      complianceChartsData.colors[index] ||
                                      "#6c757d"
                                    }
                                  />
                                )
                              )}
                            </Bar>
                          </BarChart>
                        </ResponsiveContainer>
                      ) : (
                        <div className="h-56 flex items-center justify-center text-gray-400 text-sm">
                          No compliance data available
                        </div>
                      )}
                    </div>
                  </div>

                  {/* All Columns Analysis */}
                  <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
                    <div className="p-4 border-b border-gray-200">
                      <h3 className="text-base font-bold text-gray-900">
                        All Columns Analysis
                      </h3>
                    </div>
                    <div className="overflow-x-auto">
                      <div className="max-h-[500px] overflow-y-auto">
                        <table className="w-full text-sm">
                          <thead className="bg-gray-50 sticky top-0">
                            <tr>
                              <th className="px-4 py-2.5 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">
                                Column
                              </th>
                              <th className="px-4 py-2.5 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">
                                PII Types
                              </th>
                              <th className="px-4 py-2.5 text-center text-xs font-bold text-gray-700 uppercase tracking-wider">
                                Risk Level
                              </th>
                              <th className="px-4 py-2.5 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">
                                Analysis Notes
                              </th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-gray-200">
                            {apiData?.pii_detection?.columns_detail &&
                            apiData.pii_detection.columns_detail.length > 0 ? (
                              apiData.pii_detection.columns_detail
                                .slice(0, 15)
                                .map((col, idx) => (
                                  <tr
                                    key={idx}
                                    className="hover:bg-blue-50 transition-colors"
                                  >
                                    <td className="px-4 py-2.5 text-xs font-semibold text-gray-900">
                                      {col.column_name || "N/A"}
                                    </td>
                                    <td className="px-4 py-2.5 text-xs text-gray-600">
                                      {col.pii_types && col.pii_types.length > 0
                                        ? col.pii_types.join(", ")
                                        : "None"}
                                    </td>
                                    <td className="px-4 py-2.5 text-xs text-center">
                                      <span
                                        className={`px-2 py-1 rounded-full font-semibold text-[10px] ${
                                          col.column_risk_level === "High"
                                            ? "bg-red-100 text-red-700"
                                            : col.column_risk_level === "Medium"
                                            ? "bg-yellow-100 text-yellow-700"
                                            : "bg-green-100 text-green-700"
                                        }`}
                                      >
                                        {col.column_risk_level || "N/A"}
                                      </span>
                                    </td>
                                    <td className="px-4 py-2.5 text-xs text-gray-600">
                                      {col.analysis_notes &&
                                      Array.isArray(col.analysis_notes) &&
                                      col.analysis_notes.length > 0
                                        ? col.analysis_notes[0]
                                        : "N/A"}
                                    </td>
                                  </tr>
                                ))
                            ) : (
                              <tr>
                                <td
                                  colSpan="4"
                                  className="px-4 py-4 text-center text-gray-500 text-xs"
                                >
                                  No columns data available
                                </td>
                              </tr>
                            )}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>

                  {/* Compliance Details */}
                  <div className="grid grid-cols-3 gap-4">
                    {apiData.compliance.gdpr && (
                      <div className="bg-white rounded-lg p-4 border border-gray-200 shadow-sm mb-2">
                        <h3 className="text-sm font-bold text-blue-900 mb-3 flex items-center gap-2">
                          <span className="w-2 h-2 bg-blue-600 rounded-full"></span>
                          GDPR Compliance
                        </h3>
                        <div className="space-y-2">
                          <div className="flex justify-between items-center">
                            <span className="text-xs text-gray-600">Score:</span>
                            <span className="text-lg font-bold text-blue-600">
                              {apiData.compliance.gdpr.percentage}%
                            </span>
                          </div>
                          <div className="flex justify-between items-center">
                            <span className="text-xs text-gray-600">Grade:</span>
                            <span className="px-2 py-1 bg-green-100 text-green-700 text-xs font-bold rounded">
                              {apiData.compliance.gdpr.grade}
                            </span>
                          </div>
                          <div className="mt-3 pt-3 border-t border-gray-200">
                            {apiData.compliance.gdpr.details
                              ?.slice(0, 3)
                              .map((detail, i) => (
                                <p key={i} className="text-xs text-gray-600 mb-1">
                                  {detail}
                                </p>
                              ))}
                          </div>
                        </div>
                      </div>
                    )}

                    {apiData.compliance.hipaa && (
                      <div className="bg-white rounded-lg p-4 border border-gray-200 shadow-sm mb-2">
                        <h3 className="text-sm font-bold text-teal-900 mb-3 flex items-center gap-2">
                          <span className="w-2 h-2 bg-teal-600 rounded-full"></span>
                          HIPAA Compliance
                        </h3>
                        <div className="space-y-2">
                          <div className="flex justify-between items-center">
                            <span className="text-xs text-gray-600">Score:</span>
                            <span className="text-lg font-bold text-teal-600">
                              {apiData.compliance.hipaa.percentage}%
                            </span>
                          </div>
                          <div className="flex justify-between items-center">
                            <span className="text-xs text-gray-600">Grade:</span>
                            <span className="px-2 py-1 bg-green-100 text-green-700 text-xs font-bold rounded">
                              {apiData.compliance.hipaa.grade}
                            </span>
                          </div>
                          <div className="mt-3 pt-3 border-t border-gray-200">
                            {apiData.compliance.hipaa.details
                              ?.slice(0, 3)
                              .map((detail, i) => (
                                <p key={i} className="text-xs text-gray-600 mb-1">
                                  {detail}
                                </p>
                              ))}
                          </div>
                        </div>
                      </div>
                    )}

                    {apiData.compliance.ccpa && (
                      <div className="bg-white rounded-lg p-4 border border-gray-200 shadow-sm mb-2">
                        <h3 className="text-sm font-bold text-orange-900 mb-3 flex items-center gap-2">
                          <span className="w-2 h-2 bg-orange-600 rounded-full"></span>
                          CCPA Compliance
                        </h3>
                        <div className="space-y-2">
                          <div className="flex justify-between items-center">
                            <span className="text-xs text-gray-600">Score:</span>
                            <span className="text-lg font-bold text-orange-600">
                              {apiData.compliance.ccpa.percentage}%
                            </span>
                          </div>
                          <div className="flex justify-between items-center">
                            <span className="text-xs text-gray-600">Grade:</span>
                            <span className="px-2 py-1 bg-green-100 text-green-700 text-xs font-bold rounded">
                              {apiData.compliance.ccpa.grade}
                            </span>
                          </div>
                          <div className="mt-3 pt-3 border-t border-gray-200">
                            {apiData.compliance.ccpa.details
                              ?.slice(0, 3)
                              .map((detail, i) => (
                                <p key={i} className="text-xs text-gray-600 mb-1">
                                  {detail}
                                </p>
                              ))}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </>
              )}
            </>
          )}
        </div>
      )}

      {/* PII Protection Tab */}
      {activeTab === "pii-protection" && (
        <PIIProtectionTab
          piiDetectionData={piiDetectionData}
          sessionId={piiSessionId}
          fileName={piiFileName}
        />
      )}
    </div>
  );
};

export default PII;

